import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation as useReactI18nextTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthProvider';
import { Loader2, Languages, ChevronDown } from 'lucide-react';
import { useLocalizedEnv } from '../hooks/useLocalizedEnv';
import { useI18n } from '../contexts/I18nProvider';
import { apiService } from '../services/api';
import { redirectToOIDCProvider } from '../utils/oidc';
import Handlebars from 'handlebars';
import { useTranslation } from '../hooks/useTranslation';
import { useTenantIcons } from '../hooks/useTenantIcons';
import * as LucideIcons from 'lucide-react';

interface OIDCButtonConfig {
  template?: string;
  classes?: string;
  imageUrl?: string;
}

interface OIDCConfig {
  enabled: boolean;
  providerName?: string;
  logoUrl?: string;
  clientId?: string;
  clientSecret?: string;
  authorizeUrl?: string;
  authority?: string;
  scope?: string;
  redirectUri?: string;
  tokenUrl?: string;
  profileUrl?: string;
  jsonPathEmail?: string;
  jsonPathName?: string;
}

export const LoginPage: React.FC = () => {
  const { t: tReactI18next } = useReactI18nextTranslation();
  const { authError, clearError } = useAuth();
  const { getVar } = useLocalizedEnv();
  const [isOIDCLoading, setIsOIDCLoading] = useState(false);
  const { currentLanguage, availableLanguages, changeLanguage, isLoading: i18nLoading } = useI18n();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  // Database login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tenants, setTenants] = useState<Array<{id: string, name: string, slug: string}>>([]);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState<string | null>(null);

  const { t } = useTranslation(undefined, selectedTenant);

  // OpenID configuration state
  const [oidcConfig, setOidcConfig] = useState<OIDCConfig | null>(null);
  const [oidcButtonConfig, setOidcButtonConfig] = useState<OIDCButtonConfig | null>(null);

  // Local login configuration state
  const [localLoginEnabled, setLocalLoginEnabled] = useState(true); // Default to enabled

  // OIDC error state
  const [oidcError, setOidcError] = useState<string | null>(null);

  // Get tenant icons
  const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
  const { getIcon } = useTenantIcons(tenantSlug);

  // Get icon components
  const Loader2Icon = getIcon('loading', 'Loader2') || Loader2;
  const LanguageIcon = getIcon('language', 'Languages') || Languages;
  const ChevronDownIcon = getIcon('chevronDown', 'ChevronDown') || ChevronDown;

  // Load configuration on component mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
        console.log('🔄 Loading tenant config for:', tenantSlug);
        const tenantData = await apiService.getTenantBySlug(tenantSlug);
        console.log('📦 Tenant data received:', tenantData);
        
        // Load OpenID configuration
        if (tenantData?.config?.openidConfig) {
          console.log('🔑 Raw openidConfig:', tenantData.config.openidConfig);
          const openidConfig = typeof tenantData.config.openidConfig === 'string'
            ? JSON.parse(tenantData.config.openidConfig)
            : tenantData.config.openidConfig;

          console.log('✅ Parsed openidConfig:', openidConfig);
          setOidcConfig(openidConfig);

          // Load button config
          if (tenantData.config.openidButtonConfig) {
            console.log('🎨 Raw openidButtonConfig:', tenantData.config.openidButtonConfig);
            const buttonConfig = typeof tenantData.config.openidButtonConfig === 'string'
              ? JSON.parse(tenantData.config.openidButtonConfig)
              : tenantData.config.openidButtonConfig;
            console.log('✅ Parsed buttonConfig:', buttonConfig);
            setOidcButtonConfig(buttonConfig);
          } else {
            console.warn('⚠️ No openidButtonConfig found in tenant data');
          }
        } else {
          console.warn('⚠️ No openidConfig found in tenant data');
        }

        // Load local login configuration
        if (tenantData?.config?.localLoginEnabled !== undefined) {
          console.log('🔐 Local login enabled:', tenantData.config.localLoginEnabled);
          setLocalLoginEnabled(tenantData.config.localLoginEnabled);
        } else {
          console.log('🔐 Local login enabled (default):', true);
          setLocalLoginEnabled(true); // Default to enabled if not configured
        }

        // Load tenants for database login
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/tenants`);
        if (response.ok) {
          const tenantList = await response.json();
          setTenants(tenantList);
          // Auto-select first tenant if available
          if (tenantList.length > 0) {
            setSelectedTenant(tenantList[0].slug);
          }
        } else {
          // Fallback to default tenant if API fails
          setTenants([{ id: 'default', name: 'Default Organization', slug: 'default' }]);
          setSelectedTenant('default');
        }
      } catch (error) {
        console.error('Failed to load configuration:', error);
        // Fallback to default tenant
        setTenants([{ id: 'default', name: 'Default Organization', slug: 'default' }]);
        setSelectedTenant('default');
      }
    };
    
    loadConfig();
  }, []);

  // Check for OIDC error in URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');

    if (error) {
      // Decode and set the error message
      const decodedError = decodeURIComponent(error);
      setOidcError(decodedError);

      // Clean up the URL by removing the error parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, []);

  const handleOIDCLogin = async () => {
    if (!oidcConfig || !oidcConfig.enabled) {
      console.error('OIDC is not enabled');
      return;
    }

    clearError(); // Clear any previous auth errors
    setOidcError(null); // Clear any previous OIDC errors
    setIsOIDCLoading(true);
    
    try {
      // Call backend to get authorization URL (keeps client secret secure!)
      // Backend will handle the callback and validation
      const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
      await redirectToOIDCProvider(tenantSlug);
    } catch (error) {
      console.error('Failed to redirect to OIDC provider:', error);
      setIsOIDCLoading(false);
    }
  };

  const handleDatabaseLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !selectedTenant) return;

    setDbError(null);
    setDbLoading(true);

    try {
      await apiService.login({ email, password, tenantSlug: selectedTenant });
      // The AuthProvider will handle the login state change
      window.location.reload(); // Force reload to update authentication state
    } catch (err) {
      setDbError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setDbLoading(false);
    }
  };

  const handleLanguageChange = async (language: string) => {
    try {
      await changeLanguage(language);
      setIsLanguageDropdownOpen(false);
    } catch (error) {
      console.error('Language change failed:', error);
    }
  };

  // Render the OpenID button with dynamic configuration
  const renderOIDCButton = useCallback(() => {
    console.log('🔍 OIDC Config:', oidcConfig);
    console.log('🔍 OIDC Button Config:', oidcButtonConfig);
    
    // Check if we have a custom template
    const hasCustomTemplate = oidcButtonConfig?.template && oidcButtonConfig.template.trim().length > 0;
    
    if (!hasCustomTemplate) {
      console.log('⚠️ No custom template found, using default button');
      // Default button if no configuration
      return (
        <button
          onClick={handleOIDCLogin}
          disabled={isOIDCLoading}
          className="w-full py-3 px-4 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isOIDCLoading ? (
            <>
              <Loader2Icon className="w-5 h-5 animate-spin mr-2" />
              {tReactI18next('common.signingIn')}
            </>
          ) : (
            <>Sign in with {oidcConfig?.providerName || 'SSO'}</>
          )}
        </button>
      );
    }

    console.log('✅ Using custom template:', oidcButtonConfig.template);
    
    // Use Handlebars to compile and render the button template
    const templateString = oidcButtonConfig.template;
    const template = Handlebars.compile(templateString);
    
    // Prepare template data
    const templateData = {
      providerName: oidcConfig?.providerName || 'SSO',
      imageUrl: oidcButtonConfig.imageUrl || oidcConfig?.logoUrl || '',
      classes: oidcButtonConfig.classes || '',
    };
    
    console.log('🎨 Template data:', templateData);
    
    // Render the template with data
    const buttonHtml = template(templateData);
    console.log('📝 Rendered HTML:', buttonHtml);

    return (
      <div
        onClick={handleOIDCLogin}
        dangerouslySetInnerHTML={{ __html: buttonHtml }}
        className={isOIDCLoading ? 'opacity-50 cursor-not-allowed pointer-events-none flex items-center justify-center' : 'cursor-pointer flex items-center justify-center'}
      />
    );
  }, [oidcButtonConfig, oidcConfig, handleOIDCLogin, isOIDCLoading, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-8 p-8">
        {/* Language Selector */}
        <div className="flex justify-end">
          <div className="relative">
            <button
              onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
              className="flex items-center space-x-1 p-2 hover:bg-muted rounded-md transition-colors"
              title={tReactI18next('common.selectLanguage')}
              disabled={i18nLoading}
            >
              <LanguageIcon className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {availableLanguages[currentLanguage] || currentLanguage}
              </span>
              <ChevronDownIcon className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${
                isLanguageDropdownOpen ? 'rotate-180' : ''
              }`} />
            </button>

            {/* Language Dropdown Menu */}
            {isLanguageDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-card border border-border rounded-lg shadow-lg z-50 py-1">
                <div className="max-h-48 overflow-y-auto">
                  {Object.entries(availableLanguages).map(([langCode, langName]) => (
                    <button
                      key={langCode}
                      onClick={() => handleLanguageChange(langCode)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                        currentLanguage === langCode ? 'bg-accent text-accent-foreground' : 'text-foreground'
                      }`}
                    >
                      {langName}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-foreground">
            {t('login.welcomeMessage')}
          </h2>
        </div>

        <div className="mt-8 space-y-6">
          {/* Database Login Form - Only show if local login is enabled */}
          {localLoginEnabled && (
            <form onSubmit={handleDatabaseLogin} className="space-y-4">
              {dbError && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md">
                  <p className="text-sm font-medium">Login Error</p>
                  <p className="text-xs mt-1">{dbError}</p>
                </div>
              )}

              {/* Tenant Selection */}
              <div className="space-y-2">
                <label htmlFor="tenant" className="text-sm font-medium text-foreground">
                  Tenant
                </label>
                <select
                  id="tenant"
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                  disabled={dbLoading}
                  required
                >
                  <option value="">Select a tenant...</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.slug}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground"
                  placeholder="admin@example.com"
                  disabled={dbLoading}
                  required
                />
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-foreground">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                  placeholder="••••••••"
                  disabled={dbLoading}
                  required
                />
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={dbLoading || !email || !password || !selectedTenant}
                className="w-full py-2 px-4 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {dbLoading ? (
                  <>
                    <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>

              {/* Demo Credentials */}
              <div className="text-center text-xs text-muted-foreground">
                <p>Demo credentials:</p>
                <p className="font-mono mt-1">admin@example.com / admin123</p>
              </div>
            </form>
          )}

          {/* Show message if no login methods are enabled */}
          {!localLoginEnabled && !oidcConfig?.enabled && (
            <div className="bg-muted/50 border border-border rounded-lg p-6 text-center">
              <p className="text-muted-foreground">
                No login methods are currently available. Please contact your administrator.
              </p>
            </div>
          )}

          {/* OpenID Login - Show if enabled */}
          {oidcConfig?.enabled && (
            <div className="space-y-4">
              {/* Show separator only if both login methods are available */}
              {localLoginEnabled && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
                  </div>
                </div>
              )}

              {(authError || oidcError) && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md">
                  <p className="text-sm font-medium">
                    {oidcError ? 'OIDC Authentication Error' : tReactI18next('common.authenticationError')}
                  </p>
                  <p className="text-xs mt-1">{oidcError || authError}</p>
                </div>
              )}

              <div className="w-full">
                {renderOIDCButton()}
              </div>

              {isOIDCLoading && (
                <p className="text-center text-sm text-muted-foreground">
                  {tReactI18next('common.redirectingToLogin')}
                </p>
              )}
            </div>
          )}
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            {tReactI18next('common.termsAgreement')}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {tReactI18next('common.accountFromCompany', { companyName: t('login.companyName') })}
          </p>
        </div>
      </div>
    </div>
  );
}; 